/**
 * Sound Manager — Lightweight audio feedback using Web Audio API.
 *
 * Synthesizes simple tones for UI feedback (no audio files needed).
 * Reads the `sound` preference from dataLayer to respect user settings.
 *
 * Usage:
 *   import { soundManager } from '../utils/soundManager'
 *   soundManager.playCorrect()
 *   soundManager.playWrong()
 *   soundManager.playLevelUp()
 *   soundManager.playStreak()
 *   soundManager.playClick()
 */

import { dataLayer } from '../data/dataLayer';
let audioContext: AudioContext | null = null;
let soundEnabled = true;

// Cache the preference — refreshed on each play call
async function isSoundEnabled(): Promise<boolean> {
  try {
    const prefs = await dataLayer.getLearnerPreferences();
    soundEnabled = prefs.sound;
    return soundEnabled;
  } catch {
    return soundEnabled;
  }
}
function getContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as {
        webkitAudioContext: typeof AudioContext;
      }).webkitAudioContext)();
    } catch {
      return null;
    }
  }
  return audioContext;
}
function playTone(frequency: number, duration: number, type: OscillatorType = 'sine', volume = 0.15, rampDown = true) {
  const ctx = getContext();
  if (!ctx) return;

  // Resume context if suspended (browser autoplay policy)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  if (rampDown) {
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  }
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}
function playChord(frequencies: number[], duration: number, type: OscillatorType = 'sine', volume = 0.1) {
  frequencies.forEach(freq => playTone(freq, duration, type, volume));
}
function playSequence(notes: {
  freq: number;
  delay: number;
  duration: number;
}[], type: OscillatorType = 'sine', volume = 0.12) {
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  notes.forEach(({
    freq,
    delay,
    duration
  }) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(freq, ctx.currentTime + delay);
    gainNode.gain.setValueAtTime(volume, ctx.currentTime + delay);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration);
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start(ctx.currentTime + delay);
    oscillator.stop(ctx.currentTime + delay + duration);
  });
}

// ── Public API ──────────────────────────────────────────────

/** Happy ascending two-note chime for correct answers */
async function playCorrect() {
  if (!(await isSoundEnabled())) return;
  playSequence([{
    freq: 523.25,
    delay: 0,
    duration: 0.12
  },
  // C5
  {
    freq: 659.25,
    delay: 0.1,
    duration: 0.2
  } // E5
  ], 'sine', 0.12);
}

/** Soft low tone for wrong answers — not punishing */
async function playWrong() {
  if (!(await isSoundEnabled())) return;
  playTone(220, 0.25, 'sine', 0.08);
}

/** Triumphant ascending arpeggio for level-ups */
async function playLevelUp() {
  if (!(await isSoundEnabled())) return;
  playSequence([{
    freq: 523.25,
    delay: 0,
    duration: 0.15
  },
  // C5
  {
    freq: 659.25,
    delay: 0.12,
    duration: 0.15
  },
  // E5
  {
    freq: 783.99,
    delay: 0.24,
    duration: 0.15
  },
  // G5
  {
    freq: 1046.5,
    delay: 0.36,
    duration: 0.3
  } // C6
  ], 'sine', 0.1);
}

/** Quick celebratory double-ping for streaks */
async function playStreak() {
  if (!(await isSoundEnabled())) return;
  playSequence([{
    freq: 880,
    delay: 0,
    duration: 0.08
  },
  // A5
  {
    freq: 1108.73,
    delay: 0.08,
    duration: 0.15
  } // C#6
  ], 'sine', 0.1);
}

/** Subtle click for UI interactions */
async function playClick() {
  if (!(await isSoundEnabled())) return;
  playTone(1200, 0.05, 'sine', 0.06);
}

/** Warm chord for daily goal reached */
async function playGoalReached() {
  if (!(await isSoundEnabled())) return;
  playChord([523.25, 659.25, 783.99], 0.4, 'sine', 0.08); // C major
}

// ── Navigation & UI Sounds (2027 Upgrade) ──────────────────────────

/** Soft pop for navigation clicks — subtle, not distracting */
async function playNavigate() {
  if (!(await isSoundEnabled())) return;
  playTone(880, 0.06, 'sine', 0.05);
}

/** Short fanfare for achievement unlocks */
async function playAchievement() {
  if (!(await isSoundEnabled())) return;
  playSequence([{
    freq: 659.25,
    delay: 0,
    duration: 0.12
  },
  // E5
  {
    freq: 783.99,
    delay: 0.1,
    duration: 0.12
  },
  // G5
  {
    freq: 1046.5,
    delay: 0.2,
    duration: 0.12
  },
  // C6
  {
    freq: 1318.51,
    delay: 0.3,
    duration: 0.3
  } // E6
  ], 'sine', 0.1);
}

/** Very short tick for counters */
async function playTick() {
  if (!(await isSoundEnabled())) return;
  playTone(1600, 0.03, 'sine', 0.03);
}

/** Ascending tones for streak milestones (3, 7, 14, 30 days) */
async function playStreakMilestone() {
  if (!(await isSoundEnabled())) return;
  playSequence([{
    freq: 440,
    delay: 0,
    duration: 0.1
  },
  // A4
  {
    freq: 554.37,
    delay: 0.08,
    duration: 0.1
  },
  // C#5
  {
    freq: 659.25,
    delay: 0.16,
    duration: 0.1
  },
  // E5
  {
    freq: 880,
    delay: 0.24,
    duration: 0.2
  } // A5
  ], 'triangle', 0.1);
}

/** Quick bloop when Djay speech bubble appears */
async function playDjaySpeak() {
  if (!(await isSoundEnabled())) return;
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume();

  // Two-tone "bloop" with pitch bend
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(600, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
  gain.gain.setValueAtTime(0.06, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.15);
}

/** Soft neutral bonk for errors/validation — not punishing */
async function playError() {
  if (!(await isSoundEnabled())) return;
  playTone(180, 0.15, 'triangle', 0.06);
}

/** Force-refresh the cached sound preference (call after settings change) */
function refreshPreference() {
  isSoundEnabled();
}
export const soundManager = {
  playCorrect,
  playWrong,
  playLevelUp,
  playStreak,
  playClick,
  playGoalReached,
  playNavigate,
  playAchievement,
  playTick,
  playStreakMilestone,
  playDjaySpeak,
  playError,
  refreshPreference
} as const;